import { TableColumnsType, Tag, Tooltip } from "antd";
import clsx from "clsx";
import dayjs from "dayjs";
import { CLAIMS, STATUS } from "src/constants";
import { DataColumnConfig } from "src/hooks/useTransformDataColumns";
import { Transaction } from "src/models/TransactionModel";
import { TRANSACTION_STATUSES } from "../../components/constants";
import CancelModal from "./CancelModal";

type DataType = Record<string, any>;

export type TransactionWithActions = Transaction & {
  actions?: void;
};

export const config: DataColumnConfig<TransactionWithActions> = {
  dateCreated: {
    searchType: "date",
  },
  transactionStatusId: {
    searchType: "select",
  },
  merchantNameEn: {
    searchType: "text",
  },
  customerId: {
    searchType: "text",
  },
  mobileNumber: {
    searchType: "text",
  },
  providerChannel: {
    searchType: "text",
  },
  userChannel: {
    searchType: "text",
  },
  siebelOrderId: {
    searchType: "text",
  },
  redemptionErrorMessage: {
    searchType: "text",
  },
  redemptionErrorCode: {
    searchType: "textWithSelect",
  },
  orderTitleEn: {
    searchType: "text",
  },
  offerNumber: {
    searchType: "text",
  },
  offerProjectValue: {
    searchType: "text",
  },
  userId: {
    searchType: "text",
  },
  externalTransactionId: {
    searchType: "text",
  },
  redeemedPoints: {
    searchType: "text",
  },
  email: {
    searchType: "text",
  },
  membershipId: {
    searchType: "text",
  },
  dateModified: {
    searchType: "date",
  },
  voucherExpiryDate: {
    searchType: "date",
  },
  creditDate: {
    searchType: "date",
  },
  actions: {
    claims: CLAIMS.CANCEL,
  },
};

export const columns: TableColumnsType<Transaction & { actions?: void }> = [
  {
    title: "Date Created",
    key: "dateCreated",
    dataIndex: "dateCreated",
    ellipsis: true,
    sorter: (a: DataType, b: DataType) =>
      new Date(a.date).getTime() - new Date(b.date).getTime(),
    render: (value) => (
      <span>{value ? dayjs(value).format("DD-MM-YYYY h:mm A") : "_"}</span>
    ),
  },
  {
    title: "Status",
    key: "transactionStatusId",
    dataIndex: "transactionStatusId",
    ellipsis: true,
    render: (value: number) => {
      return (
        <Tag
          className={clsx({
            "rounded-sm text-12 border-0 font-bold uppercase": true,
            "bg-[#000000]/20 text-[#000000]": value === STATUS.NONE,
            "bg-[#18a845]/20 text-[#18a845]": value === STATUS.SUCCESS,
            "bg-[#1FB981]/20 text-[#1FB981]": value === STATUS.EARNED,
            "bg-[#FBBF24]/20 text-[#FBBF24]": value === STATUS.PENDING,
            "bg-[#E10A0A]/20 text-[#E10A0A]":
              value === STATUS.ERROR ||
              value === STATUS.WARNING ||
              value === STATUS.FAILED,
          })}
        >
          {TRANSACTION_STATUSES[value]}
        </Tag>
      );
    },
  },
  {
    title: "Merchant Name",
    key: "merchantNameEn",
    dataIndex: "merchantNameEn",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Customer ID",
    key: "customerId",
    dataIndex: "customerId",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Mobile Number",
    key: "mobileNumber",
    dataIndex: "mobileNumber",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Provider Channel",
    key: "providerChannel",
    dataIndex: "providerChannel",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "User Channel",
    key: "userChannel",
    dataIndex: "userChannel",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Order ID",
    key: "siebelOrderId",
    dataIndex: "siebelOrderId",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Redemption Error Message",
    key: "redemptionErrorMessage",
    dataIndex: "redemptionErrorMessage",
    ellipsis: true,
    render: (value) => (
      <Tooltip trigger={["click"]} title={value && value}>
        <span className="max-w-[250px] inline-block truncate">
          {value ? value : "_"}
        </span>
      </Tooltip>
    ),
  },
  {
    title: "Redemption Error Code",
    key: "redemptionErrorCode",
    dataIndex: "redemptionErrorCode",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Order Title",
    key: "orderTitleEn",
    dataIndex: "orderTitleEn",
    ellipsis: true,
    render: (value) => (
      <Tooltip trigger={["click"]} title={value && value}>
        <span className="max-w-[250px] inline-block truncate">
          {value ? value : "_"}
        </span>
      </Tooltip>
    ),
  },
  {
    title: "Offer Number",
    key: "offerNumber",
    dataIndex: "offerNumber",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Offer Project Value",
    key: "offerProjectValue",
    dataIndex: "offerProjectValue",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "User ID",
    key: "userId",
    dataIndex: "userId",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "External Transaction Id",
    key: "externalTransactionId",
    dataIndex: "externalTransactionId",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Redeemed Points",
    key: "redeemedPoints",
    dataIndex: "redeemedPoints",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Value",
    key: "sarValue",
    dataIndex: "sarValue",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Offer Discount",
    key: "offerDiscount",
    dataIndex: "offerDiscount",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Email",
    key: "email",
    dataIndex: "email",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Request ID",
    key: "requestId",
    dataIndex: "requestId",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Secondary Email",
    key: "secondaryEmail",
    dataIndex: "secondaryEmail",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Merchant ID",
    key: "externalMerchantId",
    dataIndex: "externalMerchantId",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Membership ID",
    key: "membershipId",
    dataIndex: "membershipId",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Date Modified",
    key: "dateModified",
    dataIndex: "dateModified",
    ellipsis: true,
    render: (value) => (
      <span>{value ? dayjs(value).format("DD/MM/YYYY hh:mmA") : "_"}</span>
    ),
  },
  {
    title: "Voucher Expiry Date",
    key: "voucherExpiryDate",
    dataIndex: "voucherExpiryDate",
    ellipsis: true,
    render: (value) => (
      <span>{value ? dayjs(value).format("DD/MM/YYYY hh:mmA") : "_"}</span>
    ),
  },
  {
    title: "Credit Date",
    key: "creditDate",
    dataIndex: "creditDate",
    ellipsis: true,
    render: (value) => (
      <span>{value ? dayjs(value).format("DD/MM/YYYY hh:mmA") : "_"}</span>
    ),
  },
  {
    title: "Is Points Redeemed",
    key: "isPointsRedeemed",
    dataIndex: "isPointsRedeemed",
    ellipsis: true,
    render: (value) => <span>{value ? "Yes" : "No"}</span>,
  },
  {
    title: "Is Voucher Subscribed",
    key: "isVoucherSubscribed",
    dataIndex: "isVoucherSubscribed",
    ellipsis: true,
    render: (value) => <span>{value ? "Yes" : "No"}</span>,
  },
  {
    title: "Is Pending",
    key: "isPending",
    dataIndex: "isPending",
    ellipsis: true,
    render: (value) => <span>{value ? "Yes" : "No"}</span>,
  },
  {
    title: "Offer Id",
    key: "externalOfferId",
    dataIndex: "externalOfferId",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Voucher Code",
    key: "voucherCode",
    dataIndex: "voucherCode",
    ellipsis: true,
    render: (value) => <span>{value ? value : "_"}</span>,
  },
  {
    title: "Actions",
    key: "actions",
    fixed: "right",
    ellipsis: true,
    className: "bg-white",
    render: (record: any) => <CancelModal record={record} />,
  },
];
